import { Controller } from "@hotwired/stimulus";
import ahoy from "ahoy.js";
import { snakeCase, keys, includes } from "es-toolkit/compat";
import { pushUniversalEvent } from "@/lib/gtm";

type TrackingData = Record<string, string>;

const EXCLUDED_TRACKING_KEYS = ["controller", "action", "trackOn", "trackOnce"] as const;

export default class extends Controller {
  declare stage: string | null;
  declare role: string | null;
  declare trackingData: TrackingData;
  declare tracked: boolean;

  connect(): void {
    this.initializeTrackingData();
    this.tracked = false;

    // Auto-track on connect if data-track-on="connect"
    const trackOn = (this.element as HTMLElement).dataset.trackOn;
    if (trackOn === "connect") {
      this.track();
    }
  }

  track(event?: Event): void {
    if (this.trackOnce && this.tracked) return;
    this.tracked = true;

    const name = this.eventName(event) || this.role || this.trackingData["name"];
    if (name) {
      ahoy.track(name, this.trackingData);
      pushUniversalEvent(name, { ...this.trackingData, stage: this.stage });
    }
  }

  private eventName(event?: Event): string | null {
    if (event == null) return null;
    const detail = (event as CustomEvent).detail;
    if (detail == null || typeof detail !== "object") return null;
    return (detail as { name?: string }).name ?? null;
  }

  private get trackOnce(): boolean {
    return (this.element as HTMLElement).dataset.trackOnce === "true";
  }

  private initializeTrackingData(): void {
    this.stage = this.getMetaContent("rails_stage");
    const element = this.element as HTMLElement;
    this.trackingData = this.convertKeysToSnakeCase(element.dataset);
    this.role = element.role ?? null;
  }

  private getMetaContent(name: string): string | null {
    return (
      (document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement)
        ?.content ?? null
    );
  }

  private convertKeysToSnakeCase(obj: DOMStringMap): TrackingData {
    return keys(obj).reduce<TrackingData>((acc, key) => {
      if (includes(EXCLUDED_TRACKING_KEYS, key)) {
        return acc;
      }

      const snakeCaseKey = snakeCase(key);
      if (obj[key]) {
        acc[snakeCaseKey] = obj[key];
      }
      return acc;
    }, {});
  }
}
